import { Button } from '@monksflow/monks-ui';
import { Plus } from 'lucide-react';

import { EmptyState } from '@/shared/ui/EmptyState';
import type { SessionsViewState as ViewState } from './SessionsViewState.types';
import { useTranslation } from 'react-i18next';

interface SessionsViewStateProps {
  state: ViewState;
}

/**
 * Renders the appropriate UI for each Sessions list view state.
 *
 * Presentational only — receives a discriminated union and renders
 * the matching state. No hooks, no business logic.
 */
export function SessionsViewState({ state }: SessionsViewStateProps) {
  const { t } = useTranslation('chats');

  switch (state.type) {
    case 'error':
      return (
        <EmptyState
          className="flex-1"
          role="alert"
          title={t('sessions.sessionsViewState.dataFailedToLoad')}
          description={t('sessions.sessionsViewState.anErrorOccurredAndThe')}
        />
      );

    case 'no-results':
      return (
        <EmptyState
          className="flex-1"
          title={t('sessions.sessionsViewState.noMatchingSessions')}
          description={t('sessions.sessionsViewState.noSessionsMatchYourCurrent')}
        >
          <Button variant="outline" size="default" onClick={state.onClear}>
            {t('sessions.sessionsViewState.clearSearch')}
          </Button>
        </EmptyState>
      );

    case 'empty':
      return (
        <EmptyState
          className="flex-1"
          title={t('sessions.sessionsViewState.noSessionStartedYet')}
          description={t('sessions.sessionsViewState.navigateToYourDashboardAnd')}
        >
          <Button size="default" onClick={state.onAction}>
            <Plus className="size-4" />
            {t('sessions.sessionsViewState.newSession')}
          </Button>
        </EmptyState>
      );

    case 'data':
      return null; // Data state is rendered by the parent (toolbar + table)
  }
}
